import { useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Label, Tooltip } from "flowbite-react";
import DatePicker from "react-datepicker";
import toast from "react-hot-toast";
import {
  HiChevronDown,
  HiChevronUp,
  HiInformationCircle,
  HiLink,
  HiOutlineBriefcase,
  HiQuestionMarkCircle,
  HiTrash,
} from "react-icons/hi";
import { MdAccountTree } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { useOrganizationService, useProjectService, useResourcesService, } from "../../../services";
import { resetTaskData } from "../../../redux/slices/projects";
import { formatDate, removeBlankKeyfromObject, } from "../../../helpers/data-format";
import { useJwt } from "../../../helpers/common";
import { Drawer } from "../../../components/Modals";
import TaskDeleteModal from "../../../components/Modals/TaskDeleteModal";
import moment from "moment";
import RichTextEditor from "../../../components/RichTextEditor";
import CustomButton from "../../../components/common/CustomButton";
import { useResourceInfo } from "../../../hooks/useResourceInfo";
import { getDefaultUserName } from "../../../constants/defaultUsers";
import FormInput from "../../../components/common/FormInput";
import CustomDropdown from "../../../components/common/CustomDropdown";
import NumberInput from "../../../components/common/NumberInput";
import MultiSelect from "../../../components/common/MultiSelect";
import ResourceSelectWithRecommendations from "../../../components/common/ResourceSelectWithRecommendations";
import ResourceMultiSelectWithRecommendations from "../../../components/common/ResourceMultiSelectWithRecommendations";
import { AiOutlineTeam } from "react-icons/ai";
import { dashboardStatusHexColors, getDashboardStatusLabel, } from "../../../constants/integrationTypes";
import UnsavedChangesIndicator from "../../../components/common/UnsavedChangesIndicator";
import Portal from "../../../components/Portal";
import PredecessorDropdown from "./PredecessorDropdown";
import CustomTooltip from "../../../components/common/CustomTooltip";

interface TaskCompletionUpdateModalProps {
  project_id?: any;
  selectedTask?: any;
  showModal?: any;
  onClose?: any;
  handleAddNewTask?: any;
  isVisiable?: any;
  tasks?: any;
  members?: any[];
  sim_flag?: any;
  isToken?: any;
  token?: any;
  openProject?: () => void;
  onTaskUpdate?: any;
  isUnallocatedTask?: boolean;
}

const TaskCompletionUpdateModal = ({
  onClose,
  onTaskUpdate,
  project_id,
  selectedTask,
  isVisiable,
  handleAddNewTask,
  tasks,
  members,
  openProject,
  sim_flag,
  isToken = false,
  isUnallocatedTask = false,
}: TaskCompletionUpdateModalProps) => {
  const dispatch = useDispatch();
  const { getResourceName } = useResourceInfo();
  const { updateTaskCompletion, createTask, fetchTask, deleteTask } =
    useProjectService();
  const { fetchOrganizationSkill } = useOrganizationService();
  const { getResourcesbyIds, getRecommendedResources } = useResourcesService();

  const JWTdata: any = useJwt();
  const org_id = JWTdata.org_id;
  const { getAzureSettings, getJiraSettings, fetchSprints } =
    useOrganizationService();

  const { task = {}, taskLoading } = useSelector(
    (state: any) => state.projects
  );
  const { template_id } = useSelector((state: any) => state.templates);
  const isTemplate = !!template_id;

  const {
    skills = [],
    skillLoading,
    orgSchedule = [],
  } = useSelector((state: any) => state.organization);
  const { azureSettings, azureSettingsLoading } = useSelector(
    (state: any) => state.organization
  );
  const { jiraSettings, jiraSettingsLoading, orgDetails } = useSelector(
    (state: any) => state.organization
  );
  const resourcesbyIdsLoading = useSelector(
    (state: any) => state.resources.loading
  );
  const resourcesDataArr = useSelector(
    (state: any) => state.resources.resourcesData
  );
  const { start_date: projectStartDate, loading: isDataApiLoading, entity_type } =
    useSelector((state: any) => state.roadmap);
  const { dashboardDetails, projectName } = useSelector((state: any) => state.timeline);

  const [completion, setCompletion] = useState(selectedTask?.completion || 0);
  const [description, setDescription] = useState(selectedTask?.description);
  const [task_title, setTitle] = useState(selectedTask?.task_title);
  const [skill, setSkill] = useState(0);
  const [duration, setDuration] = useState(selectedTask?.duration);
  const [responsible, setResponsible] = useState(selectedTask?.responsible);
  const [start_date, setStartDate] = useState(selectedTask?.start_date);
  const [delayed_date, setDelayedDate] = useState(selectedTask?.start_date);
  const [task_type, setTask_type] = useState(selectedTask?.task_type || "task");
  const [dependenciesIds, setDependenciesIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [bid_price, setBidPrice] = useState(selectedTask?.bid_price);
  const [billing_price, setBillingPrice] = useState(
    selectedTask?.billing_price
  );
  const [additional_resources, setAdditionalResources] = useState<string[]>([]);
  const [additionalResourceArr, setAdditionalResourceArr] = useState([]);
  const [filterResponsibleArr, setFilterResponsibleArr] = useState([]);
  const [orgScheduleArr, setOrgScheduleArr] = useState([]);
  const [float_start_date, setFloatStartDate] = useState(
    selectedTask?.float_start_date
  );
  const [due_date, setDueDate] = useState(selectedTask?.due_date);
  const [priority, setPriority] = useState(selectedTask?.priority);
  const [showProjectStatesModal, setShowProjectStatesModal] = useState(false);
  const [projectStatesDropdownRef, setProjectStatesDropdownRef] =
    useState<HTMLDivElement | null>(null);
  const [stageClickCounts, setStageClickCounts] = useState<
    Record<string, number>
  >({});
  const [showStageInfoTooltip, setShowStageInfoTooltip] = useState(false);
  const [showSprintInfoModal, setShowSprintInfoModal] = useState(false);
  const [sprintInfoDropdownRef, setSprintInfoDropdownRef] =
    useState<HTMLDivElement | null>(null);
  const [sprintInfoLoading, setSprintInfoLoading] = useState(false);
  const [sprintInfoData, setSprintInfoData] = useState<any[]>([]);
  const [projectStatesAlignRight, setProjectStatesAlignRight] = useState(false);
  const [sprintInfoAlignRight, setSprintInfoAlignRight] = useState(false);
  const [sprintDataLoaded, setSprintDataLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isDataSyncedRef = useRef<boolean>(false);
  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRecommendedResourcesFetchRef = useRef<string>('');
  const lastSkillForRecommendedRef = useRef<string | number | null>(null);

  const statusFromSelected = selectedTask?.status;
  const statusFromList = (() => {
    if (!Array.isArray(tasks)) return undefined;
    const lookupId = selectedTask?.item_id ?? selectedTask?.id;
    if (!lookupId) return undefined;
    const found = tasks.find((t: any) => (t?.item_id ?? t?.id) === lookupId);
    return found?.status;
  })();
  const effectiveStatusLower =
    (statusFromSelected ?? statusFromList ?? task?.status)?.toLowerCase?.() ||
    "";
  const statusLabel = getDashboardStatusLabel(effectiveStatusLower);
  const statusHexColor =
    dashboardStatusHexColors[effectiveStatusLower] || "#a3a3a3";

  const [validationErrors, setValidationErrors] = useState({
    title: false,
    description: false,
    skill: false,
    start_date: false,
  });
  const [apiFieldErrors, setApiFieldErrors] = useState({
    dependencies: "",
  });

  const findProjectMappings = () => {
    const integrationTags = task?.integration_data?.integration_tags;
    const projectName = integrationTags?.project_name?.toLowerCase();
    const teamName = integrationTags?.team_name?.toLowerCase();

    const provider =
      task?.integration_data?.integration_type?.toLowerCase() === "jira"
        ? "jira"
        : "azure";
    const settingsSource = provider === "jira" ? jiraSettings : azureSettings;

    if (!settingsSource?.status_stages || !projectName || !teamName) return [];

    const matchedEntries = Object.entries(settingsSource.status_stages)
      .filter(([projectKey]) => projectKey.toLowerCase().includes(projectName))
      .flatMap(([projectKey, projectValue]) => {
        if (typeof projectValue !== "object") return [];

        return Object.entries(projectValue as Record<string, any>)
          .filter(([teamKey]) => teamKey.toLowerCase().includes(teamName))
          .map(([teamKey, teamValue]) => {
            const orderMapping =
              settingsSource.status_stages_order?.[projectKey]?.[teamKey] || {};

            const sortedStages = Object.keys(teamValue).sort((a, b) => {
              const orderA = orderMapping[a] ?? Number.MAX_SAFE_INTEGER;
              const orderB = orderMapping[b] ?? Number.MAX_SAFE_INTEGER;
              return orderA - orderB;
            });

            const sortedMappings = sortedStages.map((stage) => ({
              stage,
              value: teamValue[stage],
            }));

            return {
              type: "Project Team",
              name: `${projectKey} - ${teamKey}`,
              project_name: projectKey,
              team_name: teamKey,
              mappings: sortedMappings,
            };
          });
      });

    return matchedEntries;
  };

  const findMatchingSprints = async () => {
    const integrationTags = task?.integration_data?.integration_tags;
    const iterationPath = integrationTags?.iteration_path;
    const teamName = integrationTags?.team_name;

    if (!iterationPath || !teamName) return [];

    try {
      const integrationType =
        task?.integration_data?.integration_type?.toLowerCase();
      const provider = integrationType === "jira" ? "jira" : "azure";
      const allSprints = await fetchSprints(provider);

      const normalizePath = (path: string) =>
        path?.trim().replace(/\\/g, "/").toLowerCase();

      const matchingSprints = allSprints.filter((sprint: any) => {
        const sprintPath = normalizePath(sprint.path);
        const sprintTeamName = sprint.team_name?.toLowerCase().trim();
        const normalizedIterationPath = normalizePath(iterationPath);
        const normalizedTeamName = teamName?.toLowerCase().trim();

        const pathMatch =
          sprintPath &&
          normalizedIterationPath &&
          sprintPath === normalizedIterationPath;

        const teamMatch =
          sprintTeamName &&
          normalizedTeamName &&
          sprintTeamName === normalizedTeamName;

        return pathMatch && teamMatch;
      });

      return matchingSprints;
    } catch (error) {
      console.error("Error fetching sprints:", error);
      return [];
    }
  };

  // Helper function to get the dynamic child entity type from dashboard state
  const getChildEntityType = () => {
    if (dashboardDetails?.childEntityType) {
      return dashboardDetails.childEntityType;
    }

    // Default fallback
    return "Tasks";
  };

  const internalPriorities = useMemo(
    () => orgDetails?.priorities ?? [],
    [orgDetails?.priorities]
  );

  const internalPriorityOptions = useMemo(() => {
    return internalPriorities.map((p: any, i: any) => ({
      value: p.id,
      label: `P${i + 1}`,
      color: p.color,
    }));
  }, [internalPriorities]);

  // Helper function to convert plural to singular
  const getSingularEntityType = (entityType: string) => {
    if (!entityType) return "Task";

    const lower = entityType.toLowerCase();

    // Special case: Tasks should become Task
    if (lower === "tasks") {
      return "Task";
    }

    // Words ending in -ies (e.g., Stories -> Story, Companies -> Company)
    if (lower.endsWith("ies")) {
      return entityType.slice(0, -3) + "y";
    }

    // Words ending in -es after s, x, z, ch, sh (e.g., Boxes -> Box, Dishes -> Dish)
    if (
      lower.endsWith("es") &&
      (lower.endsWith("ses") ||
        lower.endsWith("xes") ||
        lower.endsWith("zes") ||
        lower.endsWith("ches") ||
        lower.endsWith("shes"))
    ) {
      return entityType.slice(0, -2);
    }

    // Words ending in -ves (e.g., Lives -> Life, Knives -> Knife)
    if (lower.endsWith("ves")) {
      return entityType.slice(0, -3) + "fe";
    }

    // Words ending in -s but not -ss (e.g., Tasks -> Task, but not Glass -> Glas)
    if (lower.endsWith("s") && !lower.endsWith("ss")) {
      return entityType.slice(0, -1);
    }

    // Return as-is if no plural pattern matches
    return entityType;
  };

  const childEntityType = getChildEntityType();
  const singularEntityType = getSingularEntityType(childEntityType);

  const unsavedChanges = useMemo(() => {
    if (!task?.item_id || taskLoading || !task || Object.keys(task).length === 0 || !isDataSyncedRef.current) {
      return false;
    }

    if (!task.task_title) {
      return false;
    }
    const normalizeDescription = (desc: string) => {
      if (!desc) return "";
      try {
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = desc;
        const textContent = tempDiv.textContent || tempDiv.innerText || "";
        let normalized = textContent.replace(/\s+/g, " ");
        normalized = normalized.replace(/\s+([,.!?;:])/g, "$1");
        return normalized.trim();
      } catch (error) {
        console.warn(
          "DOM-based HTML parsing failed, using regex fallback:",
          error
        );
        let normalized = desc.replace(/<[^>]*>/g, "");
        normalized = normalized.replace(/\s+/g, " ");
        normalized = normalized.replace(/\s+([,.!?;:])/g, "$1");
        return normalized.trim();
      }
    };

    const normalizeTitle = (title: string) => {
      if (!title) return "";
      return title.replace(/\s+/g, " ").trim();
    };

    const originalTitle = normalizeTitle(task.task_title || "");
    const currentTitle = normalizeTitle(task_title || "");
    if (originalTitle !== currentTitle) {
      return true;
    }

    const currentDescRaw = description || "";
    const initialDescRaw = task.description || "";
    const isInitialLoad = !currentDescRaw && initialDescRaw;

    const currentDescNormalized = normalizeDescription(currentDescRaw);
    const initialDescNormalized = normalizeDescription(initialDescRaw);
    const descExactMatch = currentDescNormalized === initialDescNormalized;
    const descContentMatch =
      currentDescNormalized.replace(/\s/g, "") ===
      initialDescNormalized.replace(/\s/g, "");

    const descriptionChanged =
      !isInitialLoad && !descExactMatch && !descContentMatch;
    if (descriptionChanged) {
      return true;
    }

    const originalSkillId = task.skill_id ? String(task.skill_id) : "";
    const currentSkillId = skill ? String(skill) : "";
    if (originalSkillId !== currentSkillId) {
      return true;
    }

    if (Number(task.duration || 0) !== Number(duration || 0)) {
      return true;
    }

    const originalResponsible = task.responsible
      ? String(task.responsible)
      : "";
    const currentResponsible = responsible ? String(responsible) : "";
    if (originalResponsible !== currentResponsible) {
      return true;
    }

    if ((task.task_type || "") !== (task_type || "")) {
      return true;
    }

    if (Number(task.completion || 0) !== Number(completion || 0)) {
      return true;
    }

    const formatDateValue = (val: any) => {
      if (!val) return "";
      if (val instanceof Date) return formatDate(val.toISOString());
      return formatDate(String(val));
    };

    if (task_type === "float") {
      const originalFloatDate = task.float_start_date
        ? formatDateValue(task.float_start_date)
        : "";
      const currentFloatDate = float_start_date
        ? formatDateValue(float_start_date)
        : "";
      if (originalFloatDate !== currentFloatDate) {
        return true;
      }
    }

    // Always compare delayed_date (Adjusted Start Date) with task.start_date
    const originalStartDate = task.start_date
      ? formatDateValue(task.start_date)
      : "";
    const currentStartDate = delayed_date
      ? formatDateValue(delayed_date)
      : "";
    if (originalStartDate !== currentStartDate) {
      return true;
    }

    const originalDueDate = task.due_date ? formatDateValue(task.due_date) : "";
    const currentDueDate = due_date ? formatDateValue(due_date) : "";
    if (originalDueDate !== currentDueDate) {
      return true;
    }

    const originalDeps = (task.dependency || [])
      .map((id: any) => String(id))
      .sort();
    const currentDeps = (dependenciesIds || [])
      .map((id: any) => String(id))
      .sort();
    if (originalDeps.length !== currentDeps.length) {
      return true;
    }
    for (let i = 0; i < originalDeps.length; i++) {
      if (originalDeps[i] !== currentDeps[i]) {
        return true;
      }
    }

    const originalAdditional = (task.additional_resources || [])
      .map((id: any) => String(id))
      .sort();
    const currentAdditional = (additional_resources || [])
      .map((id: any) => String(id))
      .sort();
    if (originalAdditional.length !== currentAdditional.length) {
      return true;
    }
    for (let i = 0; i < originalAdditional.length; i++) {
      if (originalAdditional[i] !== currentAdditional[i]) {
        return true;
      }
    }

    if (Number(task.bid_price || 0) !== Number(bid_price || 0)) {
      return true;
    }

    if (Number(task.billing_price || 0) !== Number(billing_price || 0)) {
      return true;
    }

    const originalPriority = task.priority ? String(task.priority) : "";
    const currentPriority = priority ? String(priority) : "";
    if (originalPriority !== currentPriority) {
      return true;
    }

    return false;
  }, [
    task,
    taskLoading,
    task_title,
    description,
    skill,
    duration,
    responsible,
    task_type,
    completion,
    float_start_date,
    delayed_date,
    due_date,
    dependenciesIds,
    additional_resources,
    bid_price,
    billing_price,
    priority,
  ]);

  const onSubmit = async () => {
    setValidationErrors({
      title: false,
      description: false,
      skill: false,
      start_date: false,
    });

    const normalizedTitle = (task_title || "").replace(/\s+/g, " ").trim();

    if (!normalizedTitle) {
      setValidationErrors((prev) => ({ ...prev, title: true }));
      toast.error("Title cannot be null/empty!", { duration: 1000 });
      return;
    }

    if (task_type === "float" && !float_start_date) {
      setValidationErrors((prev) => ({ ...prev, start_date: true }));
      toast.error("Start date is required for float tasks!", {
        duration: 1000,
      });
      return;
    }

    const strippedDescription = description?.replace(/<[^>]*>/g, "").trim();
    if (
      !description ||
      !strippedDescription ||
      description === "<p></p>" ||
      description === "<p><br></p>" ||
      description === "<p><br/></p>"
    ) {
      setValidationErrors((prev) => ({ ...prev, description: true }));
      toast.error("Description cannot be null/empty!", { duration: 1000 });
      return;
    }

    if (!skill) {
      setValidationErrors((prev) => ({ ...prev, skill: true }));
      toast.error("Please select skill type !");
      return;
    }

    if (String(duration).trim() === "0") {
      toast.error("Duration must be at least 1 day!", { duration: 1000 });
      return;
    }

    // Convert additional_resources array items to strings
    const stringAdditionalResources = additional_resources.map((id: any) =>
      id.toString()
    );

    if (task?.item_id) {
      setIsSubmitting(true);
      const UpdateTaskCompletionObject: any = {
        completion,
        skill: "",
        description,
        task_title: normalizedTitle,
        duration,
        task_type,
        responsible,
        dependencies: dependenciesIds,
        sim_flag,
        skill_id: skill ? skill.toString() : "",
        bid_price: Number(bid_price),
        billing_price: Number(billing_price),
        additional_resources: stringAdditionalResources,
      };

      if (task_type === "float" && float_start_date) {
        UpdateTaskCompletionObject["float_start_date"] =
          formatDate(float_start_date);
      } else if (start_date) {
        UpdateTaskCompletionObject["start_date"] = formatDate(start_date);
      }

      if (due_date) {
        UpdateTaskCompletionObject["due_date"] = formatDate(due_date);
      } else {
        UpdateTaskCompletionObject["due_date"] = "";
      }

      if (priority) {
        UpdateTaskCompletionObject["priority"] = priority;
      } else {
        UpdateTaskCompletionObject["priority"] = "";
      }

      const data = removeBlankKeyfromObject(UpdateTaskCompletionObject);

      if (delayed_date) {
        data["delayed_date"] = formatDate(delayed_date);
      } else {
        data["delayed_date"] = "";
      }

      data["responsible"] = responsible;

      try {
        setApiFieldErrors((prev) => ({ ...prev, dependencies: "" }));
        const res = await updateTaskCompletion(project_id, task.item_id, data, {
          suppressErrorToast: true,
          throwOnError: true,
          suppressSuccessToast: true,
        });
        if (typeof onTaskUpdate === "function") {
          onTaskUpdate();
        }
        toast.success(res?.message || "Task updated successfully");
        handleCloseModal("fromupdate");
      } catch (error: any) {
        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          "Update failed.";
        setApiFieldErrors((prev) => ({ ...prev, dependencies: message }));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsSubmitting(true);
      const taskPayload = {
        completion,
        description,
        task_title: normalizedTitle,
        task_type,
        dependencies: dependenciesIds,
        skill: "",
        duration,
        responsible,
        sim_flag,
        skill_id: skill ? skill.toString() : "",
        bid_price: Number(bid_price),
        billing_price: Number(billing_price),
        additional_resources: stringAdditionalResources,
      };
      if (task_type === "float" && float_start_date) {
        taskPayload["float_start_date"] = formatDate(float_start_date);
      }
      if (due_date) {
        taskPayload["due_date"] = formatDate(due_date);
      }
      if (priority) {
        taskPayload["priority"] = priority;
      }
      try {
        await createTask(project_id, taskPayload);
        if (typeof onTaskUpdate === "function") {
          onTaskUpdate();
        }
        toast.success("Task created successfully");
        handleCloseModal("fromupdate");
      } catch (error: any) {
        const message =
          error?.response?.data?.detail ||
          error?.response?.data?.error ||
          error?.message ||
          "Create failed.";
        setApiFieldErrors((prev) => ({ ...prev, dependencies: message }));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleDeleteTask = (status: boolean) => {
    setShowDeleteConfirm(status);
  };

  const submitDeleteTask = async () => {
    if (Object.keys(selectedTask).length) {
      setIsDeleting(true);
      try {
        let item_id = selectedTask.id;
        if (selectedTask.item_id) {
          item_id = selectedTask.item_id;
        }
        await deleteTask(project_id, item_id, sim_flag);
        handleCloseModal("fromupdate");
        onTaskUpdate();
      } catch (error) {
        console.error("Error deleting task:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  useEffect(() => {
    if (task && Object.keys(task).length > 0 && Object.keys(selectedTask).length && !taskLoading) {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
      isDataSyncedRef.current = false;
      setCompletion(task?.completion)
      setDescription(task?.description)
      setTitle(task?.task_title)
      setTask_type(task?.task_type)

      if (task?.task_type === "float") {
        setFloatStartDate(
          task?.float_start_date ? new Date(task?.float_start_date) : null
        );
      }

      if (task?.start_date) {
        setDelayedDate(`${task?.start_date}`);
      }

      setResponsible(task?.responsible);
      setDuration(task?.duration);
      setBidPrice(task?.bid_price);
      setBillingPrice(task?.billing_price);
      setSkill(task?.skill_id);
      if (task?.dependency) {
        setDependenciesIds(task?.dependency);
      }
      if (task?.additional_resources) {
        setAdditionalResources(task?.additional_resources);
      }
      setDueDate(task?.due_date || '')
      setPriority(task?.priority || '')
      syncTimeoutRef.current = setTimeout(() => {
        isDataSyncedRef.current = true;
      }, 250);
    } else if (!task || Object.keys(task).length === 0) {
      isDataSyncedRef.current = false;
    }
    return () => {
      if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    };
  }, [task, resourcesbyIdsLoading, taskLoading, selectedTask])

  useEffect(() => {
    return () => handleCloseModal();
  }, []);

  useEffect(() => {
    const orgScheduleArr: any = [];
    if (orgSchedule.length) {
      orgSchedule.forEach((element: { id: any; date: any }) => {
        orgScheduleArr.push(formatDate(element.date));
      });
      setOrgScheduleArr(orgScheduleArr);
    }
  }, [orgSchedule]);

  useEffect(() => {
    if (Object.keys(selectedTask).length && !Object.keys(task).length) {
      let item_id = selectedTask.id;
      if (selectedTask.item_id) {
        item_id = selectedTask.item_id;
      }
      if (!taskLoading) {
        fetchTask(project_id, item_id, sim_flag);
      }
    }
  }, []);

  // Fetch recommended resources after task data and skill are fully loaded
  // Also re-fetches when user manually changes the skill
  // Skip when sim_flag is active — this API is not needed in simulation mode
  useEffect(() => {
    if (sim_flag) return;
    if (taskLoading) return;
    if (!task || !Object.keys(task).length) return;
    // Wait until skill is synced from task data before calling API
    if (task?.skill_id && !skill) return;
    const taskId = task?.id || task?.item_id;
    if (!project_id || !taskId) return;

    const fetchKey = `${project_id}_${taskId}_${skill || ''}`;
    if (lastRecommendedResourcesFetchRef.current === fetchKey) return;

    lastRecommendedResourcesFetchRef.current = fetchKey;
    lastSkillForRecommendedRef.current = skill;
    getRecommendedResources(project_id, taskId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project_id, task?.id, task?.item_id, skill, sim_flag, taskLoading]);

  useEffect(() => {
    if (JWTdata.org_id) {
      fetchOrganizationSkill(JWTdata.org_id);
    }
  }, [JWTdata.org_id]);

  useEffect(() => {
    const _res = resourcesDataArr[project_id];
    if (_res && _res.length) {
      const _mapDataArr = _res
        .filter((res) => Number(res.id) !== Number(responsible))
        .map((resource) => {
          return {
            label: resource.email
              ? `${resource.name} (${resource.email})`
              : resource.name,
            value: resource.id,
          };
        });
      setAdditionalResourceArr(_mapDataArr);
      setFilterResponsibleArr(_res);
    }
  }, [resourcesDataArr, responsible, project_id]);

  const handleShowSprintInfo = async () => {
    if (!showSprintInfoModal) {
      // Show dropdown immediately
      setShowSprintInfoModal(true);

      // Load data only once
      if (!sprintDataLoaded) {
        setSprintInfoLoading(true);
        setSprintInfoData([]);

        const matchingSprints = await findMatchingSprints();
        setSprintInfoData(matchingSprints);
        setSprintInfoLoading(false);
        setSprintDataLoaded(true);
      }
    } else {
      setShowSprintInfoModal(false);
    }
  };

  const handleStageClick = (stageKey: string) => {
    const currentCount = stageClickCounts[stageKey] || 0;
    const newCount = currentCount + 1;

    setStageClickCounts((prev) => ({
      ...prev,
      [stageKey]: newCount,
    }));

    if (newCount >= 2 && newCount <= 3) {
      setShowStageInfoTooltip(true);

      setTimeout(() => {
        setShowStageInfoTooltip(false);
      }, 4000);
    }
  };

  useEffect(() => {
    const _res = resourcesDataArr[project_id];
    if (_res && _res.length) {
      const _data = _res.filter((res) => res?.skills?.includes(String(skill)));
      const _mapDataArr = _res
        .filter(
          (res) =>
            res?.skills?.includes(String(skill)) &&
            Number(res.id) !== Number(responsible)
        )
        .map((resource) => {
          return {
            label: resource.email
              ? `${resource.name} (${resource.email})`
              : resource.name,
            value: resource.id,
          };
        });
      setAdditionalResourceArr(_mapDataArr);
      setFilterResponsibleArr(_data);
    }
  }, [skill, resourcesDataArr, responsible, project_id]);





  // Load resources once when component mounts
  useEffect(() => {
    if (members && members.length && project_id && !isDataApiLoading) {
      getResourcesbyIds(project_id, {
        resource_id: members,
      });
    }
  }, [members]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        projectStatesDropdownRef &&
        !projectStatesDropdownRef.contains(event.target as Node)
      ) {
        setShowProjectStatesModal(false);
        setStageClickCounts({});
        setShowStageInfoTooltip(false);
      }
      if (
        sprintInfoDropdownRef &&
        !sprintInfoDropdownRef.contains(event.target as Node)
      ) {
        setShowSprintInfoModal(false);
      }
    };

    if (showProjectStatesModal || showSprintInfoModal) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [
    showProjectStatesModal,
    showSprintInfoModal,
    projectStatesDropdownRef,
    sprintInfoDropdownRef,
  ]);

  // Calculate dropdown positioning for Project States
  useEffect(() => {
    if (showProjectStatesModal && projectStatesDropdownRef) {
      const rect = projectStatesDropdownRef.getBoundingClientRect();
      const dropdownWidth = 320; // w-80 = 20rem = 320px
      const spaceOnRight = window.innerWidth - rect.right;
      const spaceOnLeft = rect.left;

      // If not enough space on right but enough on left, align to right edge
      setProjectStatesAlignRight(spaceOnRight < dropdownWidth && spaceOnLeft >= dropdownWidth);
    }
  }, [showProjectStatesModal, projectStatesDropdownRef]);

  // Calculate dropdown positioning for Sprint Info
  useEffect(() => {
    if (showSprintInfoModal && sprintInfoDropdownRef) {
      const rect = sprintInfoDropdownRef.getBoundingClientRect();
      const dropdownWidth = 320; // w-80 = 20rem = 320px
      const spaceOnRight = window.innerWidth - rect.right;
      const spaceOnLeft = rect.left;

      // If not enough space on right but enough on left, align to right edge
      setSprintInfoAlignRight(spaceOnRight < dropdownWidth && spaceOnLeft >= dropdownWidth);
    }
  }, [showSprintInfoModal, sprintInfoDropdownRef]);

  const handleCloseModal = (d = "") => {
    handleAddNewTask(false, d);
    setCompletion(0);
    setDescription("");
    setTask_type("");
    setSkill("");
    setDuration("");
    setPriority("");
    setBidPrice("");
    setBillingPrice("");
    setStartDate("");
    setDelayedDate("");
    setDueDate("");
    setResponsible("");
    setTitle("");
    setStageClickCounts({});
    setShowStageInfoTooltip(false);
    setShowSprintInfoModal(false);
    setSprintInfoData([]);
    setSprintDataLoaded(false);
    onClose({});
    dispatch(resetTaskData());
  };

  const handleSelectChange = (value) => {
    const ids = value.map((el) => el.value);
    setDependenciesIds(ids);
    if (ids && ids.length) {
      setApiFieldErrors((prev) => ({ ...prev, dependencies: "" }));
    }
  };

  const handleAdditionalResourcesSelect = (value) => {
    const ids = value.map((el) => el.value);
    setAdditionalResources(ids);
  };

  const handleCompletionChnage = (e: { target: { value: any } }) => {
    const val = e.target.value;
    if (val > 100) {
      setCompletion(100);
    } else if (val < 0) {
      setCompletion(0);
    } else {
      setCompletion(val);
    }
  };
  const handleDurationChange = (e: { target: { value: any } }) => {
    const rawVal = e.target.value;
    if (rawVal === "" || rawVal === null || rawVal === undefined) {
      setDuration("");
      return;
    }
    const numVal = Number(rawVal);
    if (Number.isNaN(numVal)) {
      setDuration(rawVal);
      return;
    }
    if (numVal < 1) {
      setDuration(1);
    } else {
      setDuration(numVal);
    }
  };
  const handlebidpriceChange = (e: { target: { value: any } }) => {
    const val = e.target.value;
    if (val < 0) {
      setBidPrice(0);
    } else if (val > 10000) {
      setBidPrice(10000);
    } else {
      setBidPrice(val);
    }
  };
  const handleBillingPriceChange = (e: { target: { value: any } }) => {
    const val = e.target.value;
    if (val < 0) {
      setBillingPrice(0);
    } else if (val > 10000) {
      setBillingPrice(10000);
    } else {
      setBillingPrice(val);
    }
  };

  // Sort task types
  const taskTypes = [
    { value: "task", label: "Task" },
    // { value: "todo", label: "Todo" },
    { value: "float", label: "Float" },
  ].sort((a, b) => a.label.localeCompare(b.label));

  // Sort tasks options
  const sortedTasksOption = tasks
    .slice(1)
    .filter(({ id, item_id }) => {
      const ids = id || item_id;
      const _ids = task?.id || task?.item_id;
      return ids !== _ids;
    })
    .map(({ name, id, item_id, task_title }) => {
      const ids = id || item_id;
      const _name = name || task_title;
      return {
        value: ids,
        label: `${ids} : ${_name}`,
      };
    })
    .sort((a, b) => a?.label?.localeCompare(b?.label));

  // Sort assignees
  const sortedResponsibleArr = [...filterResponsibleArr]
    .filter((r: any) => !r?.is_deleted)
    .sort((a, b) =>
      a?.name?.toLowerCase().localeCompare(b?.name?.toLowerCase())
    );

  // Sort additional resources array
  const sortedAdditionalResourceArr = [...additionalResourceArr].sort((a, b) =>
    a?.label?.toLowerCase().localeCompare(b?.label?.toLowerCase())
  );

  const isWeekday = (date: any, org_id: string) => {
    const day = date.getDay();
    const day_date: any = moment(date).format("YYYY-MM-DD");
    if (org_id && localStorage) {
      let workingDay: any = localStorage.getItem("workingDays")
        ? localStorage.getItem("workingDays")
        : "";
      let workingDays: any = workingDay ? JSON.parse(workingDay) : {};
      let orgWorkingDays: any = workingDays[org_id] ? workingDays[org_id] : [];
      if (orgScheduleArr.includes(day_date)) {
        return false;
      } else if (orgWorkingDays.length) {
        const d = day === 0 ? 7 : day;
        return orgWorkingDays.includes(d);
      } else {
        return day !== 0 && day !== 6;
      }
    } else {
      if (orgScheduleArr.includes(day_date)) {
        return false;
      } else {
        return day !== 0 && day !== 6;
      }
    }
  };

  const sortedSkills = [...skills].sort((a, b) => {
    return a?.skill?.toLowerCase().localeCompare(b?.skill?.toLowerCase());
  });

  // Check if selected assignee is deactivated
  const allProjectResources = resourcesDataArr[project_id] || [];
  const selectedAssignee = allProjectResources.find(
    (r: any) => r?.id?.toString() === responsible?.toString()
  );
  const isSelectedAssigneeDeactivated = Boolean(selectedAssignee?.is_deleted);
  const selectedAssigneeLabel = selectedAssignee
    ? selectedAssignee.email
      ? `${selectedAssignee.name} (${selectedAssignee.email})`
      : selectedAssignee.name
    : "";

  return (
    <>
      <Portal>
        <Drawer
          isOpen={isVisiable && !showDeleteConfirm}
          onClose={handleCloseModal}
          // showBackdrop={!window.location.pathname.includes("resources")}
          position="right"
        >
          <div className="relative h-[calc(100%-55px)] overflow-auto flex flex-col">
            <div
              className={`flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-6 dark:bg-tertiary-dark sticky top-0 z-40 shadow-lg rounded-b-2xl`}
            >
              {/* Show "Go to Work Unit" button if:
                  1. openProject is available (from props), OR
                  2. task is from a different project than current project_id */}
              {(openProject || (task?.project_id && task.project_id !== project_id)) && (
                <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const taskProjectId = task?.project_id || project_id;
                      const isDifferentProject = taskProjectId && taskProjectId !== project_id;

                      if (isDifferentProject) {
                        // Navigate to the different project's page
                        window.location.href = `/projects/${taskProjectId}?task-id=${task?.item_id}`;
                      } else if (project_id && task?.item_id) {
                        const uniqueTaskId = `task__${project_id}__${task.item_id}`;
                        // Open the modal (ShowSingleProjectModal) first
                        openProject?.();

                        // Fire locate-task event after modal opens and data loads
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('cadencepro:locate-task', {
                            detail: {
                              projectId: project_id,
                              taskId: task.item_id,
                              uniqueTaskId: uniqueTaskId
                            }
                          }));
                        }, 1200);
                      } else {
                        // Open modal even if no task info
                        openProject?.();
                      }
                    }}
                    className="w-full group relative overflow-hidden bg-gradient-to-br from-primary-light/10 to-primary-dark/10 hover:from-primary-light/20 hover:to-primary-dark/20 dark:from-primary-light/5 dark:to-primary-dark/5 dark:hover:from-primary-light/10 dark:hover:to-primary-dark/10 border-2 border-primary-light/30 dark:border-primary-light/20 hover:border-primary-light/50 dark:hover:border-primary-light/30 rounded-xl p-4 transition-all duration-300 ease-out hover:shadow-lg hover:shadow-primary-light/10 dark:hover:shadow-primary-light/5 focus:outline-none focus:ring-2 focus:ring-primary-light focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                    title={`Open this task in the ${entity_type || "work unit"}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-primary-light to-primary-dark flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                          <HiOutlineBriefcase className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex flex-col items-start min-w-0 flex-1">
                          {isDataApiLoading ? (
                            <div className="w-full space-y-2">
                              <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                              <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                            </div>
                          ) : (
                            <>
                              <span className="text-sm font-semibold text-light-text-primary dark:text-white group-hover:text-primary-dark dark:group-hover:text-primary-light transition-colors duration-200">
                                Go to {entity_type
                                  ? entity_type.charAt(0).toUpperCase() + entity_type.slice(1).toLowerCase()
                                  : "Work Unit"}
                              </span>
                              {(projectName || task?.project_name || selectedTask?.project_name) && (
                                <span className="text-xs text-light-text-secondary dark:text-white/60 truncate max-w-full mt-0.5">
                                  {projectName || task?.project_name || selectedTask?.project_name}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <svg
                        className="w-5 h-5 text-primary-light dark:text-primary-light/70 flex-shrink-0 group-hover:translate-x-1 transition-transform duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </div>
                  </button>
                </div>
              )}

              <div className="flex w-full items-start justify-between mb-2">
                <div className="flex flex-col min-w-0 max-w-[calc(100%-80px)]">
                  <div className="flex items-center gap-3 mb-1">
                    {taskLoading ? (
                      <div className="animate-pulse">
                        <div className="h-3.5 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-1"></div>
                      </div>
                    ) : (
                      task.item_id && (
                        <div className="text-light-text-secondary dark:text-white/70 mb-1 text-xs font-medium  leading-none tracking-wide shrink-0">
                          {task.item_id} • {task.duration} days
                          {!isTemplate && (
                            <span>
                              {" "}
                              •{" "}
                              <span
                                className="text-light-text-secondary dark:text-white/70"
                                style={{ color: statusHexColor }}
                              >
                                {statusLabel}
                              </span>
                            </span>
                          )}
                        </div>
                      )
                    )}
                  </div>

                  {taskLoading ? (
                    <div className="animate-pulse">
                      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                    </div>
                  ) : (
                    <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter']  leading-tight truncate pr-2">
                      {task.task_title
                        ? task.task_title
                        : `Create ${singularEntityType}`}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {task.item_id && !taskLoading && (
                    <button
                      type="button"
                      className="text-light-status-error-text hover:text-red-600 dark:text-red-400 dark:hover:text-red-300 hover:bg-light-status-error-bg dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                      onClick={() => handleDeleteTask(true)}
                    >
                      <HiTrash className="text-xl" />
                    </button>
                  )}

                  <button
                    type="button"
                    className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                    onClick={() => handleCloseModal()}
                  >
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </button>
                </div>
              </div>

              {taskLoading ? (
                <div className="animate-pulse mb-3">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
                </div>
              ) : (
                task.item_id && (
                  <div className="text-light-text-tertiary dark:text-white/60 text-sm mb-3">
                    {task.created_by ? (
                      <>
                        Created by{" "}
                        <span className="font-medium text-light-text-secondary dark:text-white/80">
                          {getDefaultUserName(task.created_by) ||
                            getResourceName(task.created_by)}
                        </span>{" "}
                        on{" "}
                        <span className="font-medium">
                          {moment(task.created_at).format("ll")}
                        </span>
                      </>
                    ) : task.created_at ? (
                      <>
                        Created on{" "}
                        <span className="font-medium">
                          {moment(task.created_at).format("ll")}
                        </span>
                      </>
                    ) : null}
                  </div>
                )
              )}

              {/* Integration information */}
              {taskLoading ? (
                <div className="flex flex-wrap gap-2 mt-1">
                  <Badge
                    color="indigo"
                    className="px-3 py-1 text-sm font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 border-none"
                  >
                    <div className="animate-pulse h-4 w-12"></div>
                  </Badge>
                  <Badge
                    color="info"
                    className="px-3 py-1 text-sm font-medium bg-light-status-info-bg text-light-status-info-text dark:bg-blue-900/30 dark:text-blue-300 border-none"
                  >
                    <div className="animate-pulse h-4 w-16"></div>
                  </Badge>
                  <Badge
                    color="purple"
                    className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300 border-none"
                  >
                    <div className="animate-pulse h-4 w-20"></div>
                  </Badge>
                  <Badge
                    color="success"
                    className="px-3 py-1 text-sm font-medium bg-light-status-success-bg text-light-status-success-text dark:bg-green-900/30 dark:text-green-300 border-none"
                  >
                    <div className="animate-pulse h-4 w-24"></div>
                  </Badge>
                </div>
              ) : (
                task.integration_data && (
                  <div className="flex flex-wrap gap-2">
                    {task.integration_data.integration_type && (
                      <Badge
                        color="indigo"
                        className="px-3 py-1 text-sm font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 border-none"
                      >
                        {task.integration_data.integration_type
                          .charAt(0)
                          .toUpperCase() +
                          task.integration_data.integration_type
                            .slice(1)
                            .toLowerCase()}
                      </Badge>
                    )}
                    <Badge
                      color="info"
                      className="px-3 py-1 text-sm font-medium bg-light-status-info-bg text-light-status-info-text dark:bg-blue-900/30 dark:text-white/60 border-none"
                    >
                      <a
                        href={task.integration_data.integration_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <HiLink className="mr-1" />
                        {task.integration_data.integration_id}
                      </a>
                    </Badge>
                    {task.integration_data.integration_item_type && (
                      <Badge
                        color="purple"
                        className="px-3 py-1 text-sm font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-white/60 border-none"
                      >
                        {task.integration_data.integration_item_type
                          .charAt(0)
                          .toUpperCase() +
                          task.integration_data.integration_item_type
                            .slice(1)
                            .toLowerCase()}
                      </Badge>
                    )}
                    {task.integration_data.integration_status && (
                      <Badge
                        color="success"
                        className="px-3 py-1 text-sm font-medium bg-green-100 text-light-status-success-text dark:bg-green-900/30 dark:text-green-300 border-none"
                      >
                        <span className="mr-1 font-semibold">State:</span>{" "}
                        {task.integration_data.integration_status
                          .charAt(0)
                          .toUpperCase() +
                          task.integration_data.integration_status
                            .slice(1)
                            .toLowerCase()}
                      </Badge>
                    )}
                    {/* Story Points Badge - for user story, bug, spike */}
                    {['user story', 'bug', 'spike'].includes(task.integration_data.integration_item_type?.toLowerCase()) &&
                      task.integration_data.integration_tags?.story_points && (
                        <Badge
                          color="info"
                          className="px-3 py-1 text-sm font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 border-none whitespace-nowrap"
                        >
                          <span className="inline-flex items-center gap-1">
                            <span className="font-semibold">Story Points:</span>
                            <span>{task.integration_data.integration_tags.story_points}</span>
                            {task.integration_data.integration_tags?.story_points_computed && (
                              <Tooltip
                                placement="top"
                                content="Story points computed by Cadence Pro"
                              >
                                <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                              </Tooltip>
                            )}
                            {task.integration_data.integration_tags?.completed_story_points && (
                              <>
                                <span className="text-indigo-400 dark:text-indigo-500">|</span>
                                <span className="font-semibold">Completed:</span>
                                <span>{task.integration_data.integration_tags.completed_story_points}</span>
                                {task.integration_data.integration_tags?.completed_story_points_computed ? (
                                  <Tooltip
                                    placement="top"
                                    content="Actual Story Points computed by Cadence Pro"
                                  >
                                    <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                                  </Tooltip>
                                ) : (
                                  <Tooltip
                                    placement="top"
                                    content="Actual Story Points in Azure DevOps"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="w-3 h-3 text-indigo-500 dark:text-indigo-400 cursor-help hover:text-indigo-700 dark:hover:text-indigo-200 transition-colors"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <circle cx="12" cy="12" r="10"></circle>
                                      <path d="M12 16v-4"></path>
                                      <path d="M12 8h.01"></path>
                                    </svg>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </span>
                        </Badge>
                      )}
                    {/* Estimate Badge - for task type */}
                    {task.integration_data.integration_item_type?.toLowerCase() === 'task' &&
                      task.integration_data.integration_tags?.original_estimate && (
                        <Badge
                          color="info"
                          className="px-3 py-1 text-sm font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300 border-none whitespace-nowrap"
                        >
                          <span className="inline-flex items-center gap-1">
                            <span className="font-semibold">Original Estimate:</span>
                            <span>{task.integration_data.integration_tags.original_estimate}</span>
                            {task.integration_data.integration_tags?.original_estimate_computed && (
                              <Tooltip
                                placement="top"
                                content="Original estimate computed by Cadence Pro"
                              >
                                <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                              </Tooltip>
                            )}
                            {task.integration_data.integration_tags?.completed_original_estimate && (
                              <>
                                <span className="text-indigo-400 dark:text-indigo-500">|</span>
                                <span className="font-semibold">Completed:</span>
                                <span>{task.integration_data.integration_tags.completed_original_estimate}</span>
                                {task.integration_data.integration_tags?.completed_original_estimate_computed ? (
                                  <Tooltip
                                    placement="top"
                                    content="Completed Estimate computed by Cadence Pro"
                                  >
                                    <span className="text-yellow-500 dark:text-yellow-400 cursor-help">★</span>
                                  </Tooltip>
                                ) : (
                                  <Tooltip
                                    placement="top"
                                    content="Completed estimate in Azure DevOps"
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      className="w-3 h-3 text-indigo-500 dark:text-indigo-400 cursor-help hover:text-indigo-700 dark:hover:text-indigo-200 transition-colors"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="currentColor"
                                      strokeWidth={2}
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <circle cx="12" cy="12" r="10"></circle>
                                      <path d="M12 16v-4"></path>
                                      <path d="M12 8h.01"></path>
                                    </svg>
                                  </Tooltip>
                                )}
                              </>
                            )}
                          </span>
                        </Badge>
                      )}


                    {/* Predecessor Dropdown or No Predecessors Badge */}
                    {(() => {
                      const predecessorInfo = task?.predecessor_info || [];
                      if (predecessorInfo.length > 0) {
                        return (
                          <PredecessorDropdown
                            predecessorData={predecessorInfo}
                            currentProjectId={project_id}
                            onTaskClick={(predecessorProjectId: string, taskId: string) => {
                              // Open the predecessor task in a new tab
                              window.open(`/projects/${predecessorProjectId}?task-id=${taskId}`, '_blank');
                            }}
                            taskStatus={effectiveStatusLower}
                          />
                        );
                      } else {
                        return (
                          <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                            <MdAccountTree className="w-3.5 h-3.5" />
                            No Predecessors
                          </span>
                        );
                      }
                    })()}

                    {/* Tags Badge - always show, at the end */}
                    <Badge
                      color="info"
                      className="px-3 py-1 text-sm font-medium bg-cyan-50 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-700/50 whitespace-nowrap"
                    >
                      <span className="inline-flex items-center gap-1.5">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        <span className="font-medium">Tags:</span>
                        <span>{task?.integration_data?.integration_tags?.tags || '-'}</span>
                      </span>
                    </Badge>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap items-center gap-2">
                      {(() => {
                        const provider =
                          task?.integration_data?.integration_type?.toLowerCase() ===
                            "jira"
                            ? "jira"
                            : "azure";
                        if (!["azure", "jira"].includes(provider)) return null;

                        const isProviderLoading =
                          provider === "jira"
                            ? jiraSettingsLoading
                            : azureSettingsLoading;

                        const ensureProjectStatesLoaded = () => {
                          if (!showProjectStatesModal) {
                            if (provider === "azure") {
                              if (
                                !azureSettings?.status_stages &&
                                !azureSettingsLoading
                              ) {
                                getAzureSettings(JWTdata.org_id);
                              }
                            } else {
                              if (
                                !jiraSettings?.status_stages &&
                                !jiraSettingsLoading
                              ) {
                                getJiraSettings();
                              }
                            }
                          }
                        };

                        const projectMappings = findProjectMappings();

                        return (
                          <div className="flex items-center gap-2">
                            <div
                              className="relative"
                              ref={setProjectStatesDropdownRef}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  ensureProjectStatesLoaded();
                                  setShowProjectStatesModal(
                                    !showProjectStatesModal
                                  );
                                  if (!showProjectStatesModal) {
                                    setStageClickCounts({});
                                    setShowStageInfoTooltip(false);
                                  }
                                }}
                                className="flex items-center gap-1 text-xs text-white  transition-colors duration-200 px-2 py-1 rounded-md border border-light-border-default dark:border-gray-600 hover:bg-primary-hover  bg-primary-dark shadow-lg "
                              >
                                Show Project States
                                {showProjectStatesModal ? (
                                  <HiChevronUp className="w-4 h-4" />
                                ) : (
                                  <HiChevronDown className="w-4 h-4" />
                                )}
                              </button>

                              {showProjectStatesModal && (
                                <div className={`absolute top-full border bg-light-surfaceSecondary dark:bg-secondary-dark border-light-border-default dark:border-gray-700 mt-2 w-80 rounded-lg shadow-lg z-50 max-h-96 overflow-hidden ${projectStatesAlignRight ? 'right-0' : 'left-0'}`}>
                                  <div className="">
                                    {isProviderLoading ? (
                                      <div className="space-y-3 p-3">
                                        <div className="border border-light-border-default dark:border-gray-700 rounded-lg p-3 bg-light-surfaceSecondary dark:bg-secondary-dark">
                                          <div className="mb-1">
                                            <div className="w-44 h-4 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                            </div>
                                          </div>
                                          <div className="flex justify-start my-2">
                                            <div className="h-5 w-28 bg-gray-200 dark:bg-gray-700 rounded-full relative overflow-hidden">
                                              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                            </div>
                                          </div>
                                          <div>
                                            <div className="flex items-center justify-between mb-2">
                                              <div className="w-24 h-3 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                              </div>
                                              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded relative overflow-hidden">
                                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                              </div>
                                            </div>
                                            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                                              <div className="flex items-center justify-between p-2 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                                                <div className="flex items-center space-x-3">
                                                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                  </div>
                                                  <div className="w-32 h-3 rounded bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                  </div>
                                                </div>
                                                <div className="h-5 w-16 rounded-md bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                </div>
                                              </div>
                                              <div className="flex items-center justify-between p-2 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                                                <div className="flex items-center space-x-3">
                                                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                  </div>
                                                  <div className="w-36 h-3 rounded bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                  </div>
                                                </div>
                                                <div className="h-5 w-14 rounded-md bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                </div>
                                              </div>
                                              <div className="flex items-center justify-between p-2 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                                                <div className="flex items-center space-x-3">
                                                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                  </div>
                                                  <div className="w-40 h-3 rounded bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                  </div>
                                                </div>
                                                <div className="h-5 w-12 rounded-md bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                </div>
                                              </div>
                                              <div className="flex items-center justify-between p-2 rounded-lg border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                                                <div className="flex items-center space-x-3">
                                                  <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                  </div>
                                                  <div className="w-28 h-3 rounded bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                  </div>
                                                </div>
                                                <div className="h-5 w-16 rounded-md bg-gray-200 dark:bg-gray-700 relative overflow-hidden">
                                                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      (() => {
                                        if (projectMappings.length === 0) {
                                          return (
                                            <div className="text-center px-2 py-4">
                                              <p className="text-light-text-secondary dark:text-white/70 text-sm">
                                                No project state mappings found
                                                for this task.
                                              </p>
                                            </div>
                                          );
                                        }

                                        // Get board column from task integration tags
                                        const boardColumn =
                                          task?.integration_data?.integration_tags
                                            ?.board_column;
                                        const isBoardColumnNull =
                                          !boardColumn ||
                                          boardColumn === "null" ||
                                          boardColumn === "";

                                        return (
                                          <div className="space-y-3">
                                            {projectMappings.map(
                                              (mapping, index) => (
                                                <div
                                                  key={index}
                                                  className="border border-light-border-default dark:border-gray-700 rounded-lg p-3 bg-light-surfaceSecondary dark:bg-secondary-dark"
                                                >
                                                  <div className="mb-0">
                                                    <h4 className="text-md font-semibold text-light-text-primary dark:text-white mb-1">
                                                      {mapping.team_name} ({mapping.project_name})
                                                    </h4>
                                                  </div>
                                                  <div className="flex justify-start my-1">
                                                    <span
                                                      className={`text-xs px-2 py-1 rounded-md font-medium border ${isBoardColumnNull
                                                        ? "bg-gray-100 text-gray-600  dark:bg-gray-800/60 dark:text-gray-400 border-gray-200 dark:border-gray-700"
                                                        : "bg-blue-100 text-blue-700  dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-700"
                                                        }`}
                                                    >
                                                      Board Column:{" "}
                                                      {isBoardColumnNull
                                                        ? "Not Set"
                                                        : boardColumn}
                                                    </span>
                                                  </div>

                                                  <div>
                                                    <div className="flex justify-between">
                                                      <h4 className="text-xs font-semibold text-light-text-primary dark:text-white mb-1 flex items-center">
                                                        State Mappings
                                                      </h4>
                                                      <Tooltip
                                                        placement="top"
                                                        content={
                                                          <div className="max-w-xs p-2">
                                                            <div className="font-semibold mb-1">
                                                              Project State
                                                              Mappings
                                                            </div>
                                                            <div className="text-xs">
                                                              This is read only.
                                                              To modify go to{" "}
                                                              {"Import Projects"}{" "}
                                                              {">"}{" "}
                                                              {"Project States"}
                                                            </div>
                                                          </div>
                                                        }
                                                        animation="duration-300"
                                                        trigger={"hover"}
                                                      >
                                                        <div className="">
                                                          <HiInformationCircle
                                                            className={`text-sm ml-2 transition-colors duration-200  cursor-help ${showStageInfoTooltip
                                                              ? "text-blue-500 dark:text-blue-400 animate-pulse "
                                                              : "text-primary-light dark:text-primary-dark hover:text-primary-hover"
                                                              }`}
                                                          />
                                                        </div>
                                                      </Tooltip>
                                                    </div>

                                                    <div className="space-y-2 max-h-60 overflow-y-auto ">
                                                      {Object.entries(
                                                        mapping.mappings
                                                      ).map(
                                                        (
                                                          [order, stage],
                                                          index
                                                        ) => {
                                                          const stageName =
                                                            stage?.stage;
                                                          const isMatchingStage =
                                                            !isBoardColumnNull &&
                                                            stageName &&
                                                            typeof stageName ===
                                                            "string" &&
                                                            stageName.toLowerCase() ===
                                                            boardColumn?.toLowerCase();

                                                          return (
                                                            <div
                                                              key={`stage-${index}`}
                                                              className={`flex items-center justify-between p-2 rounded-lg border transition-all duration-200 ${isMatchingStage
                                                                ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700 shadow-sm"
                                                                : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                                                                }`}
                                                            >
                                                              <div className="flex items-center space-x-3">
                                                                <div
                                                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${isMatchingStage
                                                                    ? "bg-blue-500 text-white"
                                                                    : "bg-blue-500 text-white"
                                                                    }`}
                                                                >
                                                                  {Number(order) +
                                                                    1}
                                                                </div>
                                                                <span
                                                                  className={`text-sm font-medium ${isMatchingStage
                                                                    ? "text-blue-900 dark:text-blue-100"
                                                                    : "text-gray-900 dark:text-white"
                                                                    }`}
                                                                >
                                                                  {stage?.stage}
                                                                </span>
                                                              </div>
                                                              <div className="flex items-center space-x-2">
                                                                <span
                                                                  onClick={() =>
                                                                    handleStageClick(
                                                                      stage?.stage
                                                                    )
                                                                  }
                                                                  className={`text-sm font-medium py-1 px-2 rounded-md transition-all duration-200 ${isMatchingStage
                                                                    ? "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-200 border border-blue-200 dark:border-blue-600"
                                                                    : "text-gray-900 dark:text-white"
                                                                    }`}
                                                                >
                                                                  {stage?.value}
                                                                </span>
                                                              </div>
                                                            </div>
                                                          );
                                                        }
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              )
                                            )}
                                          </div>
                                        );
                                      })()
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {task?.integration_data?.integration_tags && (
                      <div
                        className="relative"
                        ref={setSprintInfoDropdownRef}
                      >
                        <button
                          type="button"
                          onClick={handleShowSprintInfo}
                          className="flex items-center gap-1 text-xs text-white transition-colors duration-200 px-2 py-1 rounded-md border border-light-border-default dark:border-gray-600 hover:bg-primary-hover bg-primary-dark shadow-lg"
                        >
                          Show Sprint Info
                          {showSprintInfoModal ? (
                            <HiChevronUp className="w-4 h-4" />
                          ) : (
                            <HiChevronDown className="w-4 h-4" />
                          )}
                        </button>

                        {showSprintInfoModal && (
                          <div className={`absolute top-full border bg-light-surfaceSecondary dark:bg-secondary-dark border-light-border-default dark:border-gray-700 mt-2 w-80 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto ${sprintInfoAlignRight ? 'right-0' : 'left-0'}`}>
                            <div className="">
                              {sprintInfoLoading ? (
                                <div className="space-y-3 p-3">
                                  {/* Iteration Path Badge Skeleton */}
                                  <div className="mb-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <div className="w-32 h-4 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                      </div>
                                      <div className="w-16 h-6 bg-gray-200 dark:bg-gray-700 rounded-full relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                      </div>
                                    </div>
                                    <div className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                                      <div className="flex items-center gap-2 mb-2">
                                        <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded relative overflow-hidden">
                                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                        </div>
                                        <div className="w-24 h-4 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                        </div>
                                      </div>
                                      <div className="w-48 h-3 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Sprint Details Skeleton */}
                                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800">
                                    <div className="mb-3">
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="w-32 h-4 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                        </div>
                                        <div className="w-16 h-6 bg-gray-200 dark:bg-gray-700 rounded-full relative overflow-hidden">
                                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                        </div>
                                      </div>
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                          </div>
                                          <div className="w-24 h-3 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                          </div>
                                          <div className="w-28 h-3 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <div className="w-4 h-4 bg-gray-200 dark:bg-gray-600 rounded relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                          </div>
                                          <div className="w-32 h-3 bg-gray-200 dark:bg-gray-700 rounded relative overflow-hidden">
                                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer"></div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                (() => {
                                  const isJiraProvider =
                                    task?.integration_data?.integration_type?.toLowerCase?.() ===
                                    "jira";
                                  return (
                                    <div className="space-y-3 p-3">
                                      <div className="mb-3">
                                        <div className="flex items-center justify-between mb-2">
                                          <h4 className="text-md font-semibold text-light-text-primary dark:text-white">
                                            Sprint Information
                                          </h4>
                                        </div>
                                        {!isJiraProvider && (
                                          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                                            <div className="flex items-center gap-2">
                                              <svg
                                                className="w-4 h-4 text-blue-600 dark:text-blue-400"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                              >
                                                <path
                                                  strokeLinecap="round"
                                                  strokeLinejoin="round"
                                                  strokeWidth="2"
                                                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                                                ></path>
                                              </svg>
                                              <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
                                                Iteration Path
                                              </span>
                                            </div>
                                            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1 break-all">
                                              {task?.integration_data
                                                ?.integration_tags
                                                ?.iteration_path ||
                                                "Not available"}
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                      {sprintInfoData.length === 0 ? (
                                        <div className="text-center px-2 py-4">
                                          <p className="text-light-text-secondary dark:text-white/70 text-sm">
                                            No matching sprints found for this
                                            task.
                                          </p>
                                        </div>
                                      ) : (
                                        <>
                                          {sprintInfoData.map(
                                            (sprint: any, index: number) => (
                                              <div
                                                key={index}
                                                className="border border-light-border-default dark:border-gray-700 rounded-lg p-3 bg-light-surfacePrimary dark:bg-secondary-dark"
                                              >
                                                <div className="mb-3">
                                                  <div className="flex items-center justify-between mb-2">
                                                    <h5 className="text-sm font-semibold text-light-text-primary dark:text-white mb-2">
                                                      {sprint.name}
                                                    </h5>
                                                    <span
                                                      className={`text-xs px-2 py-1 rounded-full font-medium border ${sprintInfoData[0]
                                                        ?.status === "current"
                                                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-700"
                                                        : sprintInfoData[0]
                                                          ?.status === "past"
                                                          ? "bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400 border-gray-200 dark:border-gray-700"
                                                          : sprintInfoData[0]
                                                            ?.status ===
                                                            "future"
                                                            ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300 border-green-200 dark:border-green-700"
                                                            : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                                                        }`}
                                                    >
                                                      {sprintInfoData[0]
                                                        ?.status === "current"
                                                        ? "Current"
                                                        : sprintInfoData[0]
                                                          ?.status === "past"
                                                          ? "Past"
                                                          : sprintInfoData[0]
                                                            ?.status === "future"
                                                            ? "Future"
                                                            : sprintInfoData[0]
                                                              ?.status}
                                                    </span>
                                                  </div>
                                                  <div className="space-y-2">
                                                    <div className="flex items-center gap-2">
                                                      <HiOutlineBriefcase className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                      <span className="text-xs text-light-text-secondary dark:text-white/70">
                                                        {sprint.project_name}
                                                      </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <AiOutlineTeam className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                                      <span className="text-xs text-light-text-secondary dark:text-white/70">
                                                        {sprint.team_name}
                                                      </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <svg
                                                        className="w-4 h-4 text-gray-500 dark:text-gray-400"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                      >
                                                        <path
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                          strokeWidth="2"
                                                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        ></path>
                                                      </svg>
                                                      <span className="text-xs text-light-text-secondary dark:text-white/70">
                                                        {sprint.start_date
                                                          ? new Date(
                                                            sprint.start_date
                                                          ).toLocaleDateString()
                                                          : "Not set"}{" "}
                                                        -{" "}
                                                        {sprint.end_date
                                                          ? new Date(
                                                            sprint.end_date
                                                          ).toLocaleDateString()
                                                          : "Not set"}
                                                      </span>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            )
                                          )}
                                        </>
                                      )}
                                    </div>
                                  );
                                })()
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              )}
            </div>
            <div className="px-16 xs:px-4 py-6 bg-light-surfacePrimary dark:bg-tertiary-dark">
              {taskLoading ? (
                <div className="flex flex-col gap-5 max-w-4xl mx-auto animate-pulse">
                  {/* Title Field Skeleton */}
                  <div>
                    <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                      Title *
                    </div>
                    <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>

                  {/* Description Field Skeleton */}
                  <div>
                    <Label
                      className="text-sm font-medium font-['Inter'] mt-3.5 leading-tight mb-2 text-light-text-primary dark:text-white block"
                      htmlFor="description"
                    >
                      Description *
                    </Label>
                    <div className="mt-1 rounded-lg overflow-hidden">
                      <RichTextEditor
                        content="<div class='animate-pulse'><div class='h-5 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2'></div><div class='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2'></div><div class='h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2'></div></div>"
                        onChange={() => { }}
                        placeholder={`Enter ${singularEntityType.toLowerCase()} description...`}
                        disabled={false}
                      />
                    </div>
                  </div>

                  {/* Two-column grid for Skill and Task Type */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                        Skill Required *
                      </div>
                      <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                    <div>
                      <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white flex items-center">
                        Task Type *
                        <HiQuestionMarkCircle className="text-lg ml-2 text-primary-light dark:text-primary-dark hover:text-primary-hover transition-colors duration-200" />
                      </div>
                      <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                  </div>

                  {/* Dependencies Field Skeleton */}
                  <div>
                    <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                      Dependent On
                    </div>
                    <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>

                  {/* Two-column grid for Duration and Completion */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                        Duration (In Days)
                      </div>
                      <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                    <div>
                      <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                        Completion Percentage
                      </div>
                      <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                    </div>
                  </div>

                  {!isTemplate && (
                    <>
                      {/* Assignee Field Skeleton */}
                      <div>
                        <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                          Assignee
                        </div>
                        <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                      </div>

                      {/* Additional Assignees Field Skeleton */}
                      <div>
                        <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                          Additional Assignees
                        </div>
                        <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                      </div>

                      {/* Two-column grid for Bid Price and Billing Price */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div>
                          <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white flex items-center">
                            Contract Bid Price
                            <HiQuestionMarkCircle className="text-lg ml-2 text-primary-light dark:text-primary-dark hover:text-primary-hover transition-colors duration-200" />
                          </div>
                          <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                        </div>
                        <div>
                          <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white flex items-center">
                            Customer Billing Price
                            <HiQuestionMarkCircle className="text-lg ml-2 text-primary-light dark:text-primary-dark hover:text-primary-hover transition-colors duration-200" />
                          </div>
                          <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Date fields for existing tasks - only show if it's an edit */}
                  {selectedTask?.item_id && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                          Recommended Start Date
                        </div>
                        <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg opacity-60"></div>
                      </div>
                      <div>
                        <div className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white">
                          Adjusted Start Date
                        </div>
                        <div className="h-11 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-5 max-w-4xl mx-auto">
                  <FormInput
                    id="title"
                    name="title"
                    label="Title"
                    type="text"
                    value={task_title}
                    onChange={(e) => {
                      setTitle(e.target.value);
                      if (e.target.value) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          title: false,
                        }));
                      }
                    }}
                    placeholder="Enter task title..."
                    required
                    showAsterisk
                    className={` ${validationErrors.title
                      ? "border-[3px] border-light-status-error-border ring-4 ring-light-status-error-border/20 dark:border-red-500"
                      : "border border-light-border-default dark:border-gray-600"
                      } bg-light-surfaceSecondary dark:bg-slate-800 text-light-text-primary dark:text-white focus:ring-primary-light focus:border-primary-light dark:focus:ring-primary-dark dark:focus:border-primary-dark placeholder-light-input-placeholder dark:placeholder-gray-400 transition-all duration-200`}
                  />

                  <div>
                    <Label
                      className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white block"
                      htmlFor="description"
                    >
                      Description *
                    </Label>
                    <div
                      className={`mt-1 ${validationErrors.description
                        ? "border-[3px] border-light-status-error-border ring-4 ring-light-status-error-border/20 dark:border-red-500"
                        : ""
                        } rounded-lg overflow-hidden`}
                    >
                      <RichTextEditor
                        content={description}
                        onChange={(content) => {
                          setDescription(content);
                          const strippedContent = content
                            ?.replace(/<[^>]*>/g, "")
                            .trim();
                          if (strippedContent) {
                            setValidationErrors((prev) => ({
                              ...prev,
                              description: false,
                            }));
                          }
                        }}
                        placeholder={`Enter ${singularEntityType.toLowerCase()} description...`}
                        disabled={false}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <CustomDropdown
                      id="skill"
                      name="skill"
                      label="Skill Required"
                      value={skill?.toString()}
                      onChange={(value) => {
                        setSkill(value);
                        setResponsible("");
                        if (value) {
                          setValidationErrors((prev) => ({
                            ...prev,
                            skill: false,
                          }));
                        }
                      }}
                      options={sortedSkills.map((s) => ({
                        value: s.id.toString(),
                        label: s.skill,
                      }))}
                      placeholder="Choose a skill name"
                      disabled={skillLoading}
                      required
                      showAsterisk
                      className={`${validationErrors.skill
                        ? "border-[3px] border-light-status-error-border ring-4 ring-light-status-error-border/20 dark:border-red-500"
                        : "border border-light-border-default dark:border-gray-600"
                        } !bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200`}
                    />

                    <CustomDropdown
                      id="TaskType"
                      name="task_type"
                      label={
                        <span className="flex items-center">
                          Task Type *
                          <Tooltip
                            placement="top"
                            content="Task type are assigned at create. Only task type 'Tasks' shows up in Gantt view."
                          >
                            <HiQuestionMarkCircle className="text-lg ml-2 text-primary-light dark:text-primary-dark hover:text-primary-hover transition-colors duration-200" />
                          </Tooltip>
                        </span>
                      }
                      value={task_type}
                      onChange={(value) => {
                        if (task_type === "float" && value === "task") {
                          setDelayedDate("");
                        }
                        setTask_type(value);
                      }}
                      options={taskTypes}
                      placeholder="Choose a Task Type"
                      required
                      className="!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200"
                    />
                  </div>

                  {task_type !== "float" ? (
                    <div>
                      <MultiSelect
                        id="dependencies"
                        name="dependencies"
                        label="Dependent On"
                        value={sortedTasksOption.filter((ele) =>
                          dependenciesIds.includes(ele.value)
                        )}
                        onChange={handleSelectChange}
                        options={sortedTasksOption}
                        placeholder="Select dependencies"
                        className="!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200"
                      />
                      {apiFieldErrors.dependencies ? (
                        <div className="text-xs text-light-status-error-text dark:text-red-400 leading-snug whitespace-pre-wrap break-words">
                          {apiFieldErrors.dependencies}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div className="w-full">
                      <Label
                        className="block mb-2 text-sm font-medium text-light-text-primary dark:text-white"
                        htmlFor="float_start_date"
                      >
                        Start Date *
                      </Label>
                      <div className="mt-2">
                        <DatePicker
                          id="float_start_date"
                          wrapperClassName="w-full"
                          className={`w-full p-2.5 text-sm rounded-lg transition-all duration-200 ${validationErrors.start_date
                            ? "border-[3px] border-light-status-error-border ring-4 ring-light-status-error-border/20 dark:border-red-500"
                            : "border border-light-border-default dark:border-gray-600"
                            } bg-light-surfaceSecondary text-light-text-primary focus:ring-primary-light focus:border-primary-light dark:bg-slate-800 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark`}
                          selected={
                            float_start_date ? new Date(float_start_date) : null
                          }
                          onChange={(date: any) => {
                            setFloatStartDate(date);
                            if (date) {
                              setValidationErrors((prev) => ({
                                ...prev,
                                start_date: false,
                              }));
                            }
                          }}
                          filterDate={(date: Date) => isWeekday(date, org_id)}
                          minDate={
                            projectStartDate
                              ? new Date(projectStartDate)
                              : new Date()
                          }
                          autoComplete="off"
                          placeholderText="Select start date"
                          customInput={
                            <input
                              style={{ width: "100%" }}
                              className="w-full p-2.5 text-sm rounded-lg border border-light-border-default bg-light-surfaceSecondary text-light-text-primary focus:ring-primary-light focus:border-primary-light dark:bg-slate-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark transition-all duration-200"
                            />
                          }
                        />
                      </div>
                    </div>
                  )}


                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <NumberInput
                      id="duration"
                      name="duration"
                      label="Duration (In Days)"
                      value={duration}
                      onChange={handleDurationChange}
                      placeholder="Duration"
                      min={1}
                      className="!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200"
                    />

                    <div
                      title={
                        sim_flag
                          ? "Edit not allowed when simulation tab is active."
                          : ""
                      }
                    >
                      <NumberInput
                        id="completion"
                        name="completion"
                        label="Completion Percentage"
                        value={completion}
                        onChange={handleCompletionChnage}
                        placeholder="Completion Percentage"
                        min={0}
                        max={100}
                        step={5}
                        disabled={sim_flag}
                        className={`!bg-light-surfaceSecondary dark:!bg-slate-800 ${sim_flag ? "cursor-not-allowed" : ""
                          }`}
                      />
                    </div>
                  </div>

                  {!isTemplate && (
                    <>
                      <div className="w-full">
                        <ResourceSelectWithRecommendations
                          id="assignee"
                          name="assignee"
                          label="Assignee"
                          value={responsible}
                          onChange={(value) => setResponsible(value)}
                          projectMembers={resourcesDataArr[project_id] || []}
                          skillId={skill}
                          projectMemberIds={members || []}
                          projectId={project_id}
                          taskId={task?.id || task?.item_id || selectedTask?.id || selectedTask?.item_id}
                          placeholder={
                            isSelectedAssigneeDeactivated
                              ? selectedAssigneeLabel
                              : "Choose an assignee name"
                          }
                          disabled={sim_flag || isUnallocatedTask}
                          disabledMessage={sim_flag ? "Edit not allowed when simulation tab is active." : isUnallocatedTask ? "Cannot change assignee for unallocated tasks." : undefined}
                          className={`!bg-light-surfaceSecondary dark:!bg-slate-800 ${sim_flag || isUnallocatedTask ? "cursor-not-allowed" : ""} ${effectiveStatusLower === 'unallocated' ? "ring-2 ring-orange-500 border-orange-500 rounded-lg" : ""}`}
                        />
                        {isSelectedAssigneeDeactivated && (
                          <p className="text-xs text-light-text-secondary dark:text-white/70 flex items-center gap-1">
                            <HiInformationCircle className="w-3.5 h-3.5 text-light-text-tertiary dark:text-gray-400" />
                            The selected assignee is deactivated.
                          </p>
                        )}
                      </div>

                      <ResourceMultiSelectWithRecommendations
                        id="additional_assignee"
                        name="additional_assignee"
                        label="Additional Assignees"
                        selectedIds={additional_resources}
                        onChange={(ids) => setAdditionalResources(ids)}
                        projectMembers={resourcesDataArr[project_id] || []}
                        skillId={skill}
                        projectMemberIds={members || []}
                        excludeIds={responsible ? [responsible] : []}
                        projectId={project_id}
                        taskId={task?.id || task?.item_id || selectedTask?.id || selectedTask?.item_id}
                        placeholder="Select additional assignees"
                        disabled={sim_flag || isUnallocatedTask}
                        disabledMessage={sim_flag ? "Edit not allowed when simulation tab is active." : isUnallocatedTask ? "Cannot change assignees for unallocated tasks." : undefined}
                        className={`!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200 ${sim_flag || isUnallocatedTask ? "cursor-not-allowed opacity-60" : ""
                          }`}
                      />

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label
                        className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white block"
                        htmlFor="due_date"
                      >
                        Due Date
                      </Label>
                      <div className="mt-1">
                        <DatePicker
                          id="due_date"
                          className="bg-light-surfaceSecondary border border-light-border-default text-light-text-primary text-sm focus:ring-primary-light focus:border-primary-light block w-full p-2.5 dark:bg-slate-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-lg transition-all duration-200"
                          selected={due_date ? new Date(due_date) : null}
                          onChange={(date: any) => setDueDate(date)}
                          filterDate={(date) => isWeekday(date, org_id)}
                          autoComplete="off"
                          placeholderText="Select due date"
                        />
                      </div>
                    </div>

                    <div>
                      <CustomDropdown
                        id="priority"
                        name="priority"
                        label="Priority"
                        value={priority?.toString()}
                        options={internalPriorityOptions}
                        onChange={(val) => setPriority(val)}
                        placeholder="Select Priority"
                        className="!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200"
                      />
                    </div>
                  </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <NumberInput
                          id="bid_price"
                          name="bid_price"
                          label={
                            <span className="flex items-center">
                              Contract Bid Price
                              <Tooltip
                                placement="top"
                                content="Price quoted for whole task. Will override hourly cost calculation."
                              >
                                <HiQuestionMarkCircle className="text-lg ml-2 text-primary-light dark:text-primary-dark hover:text-primary-hover transition-colors duration-200" />
                              </Tooltip>
                            </span>
                          }
                          value={bid_price}
                          onChange={handlebidpriceChange}
                          placeholder="Bid price"
                          min={0}
                          max={10000}
                          disabled={sim_flag}
                          className={`!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200 ${sim_flag ? "cursor-not-allowed opacity-60" : ""
                            }`}
                        />

                        <NumberInput
                          id="billing_price"
                          name="billing_price"
                          label={
                            <span className="flex items-center">
                              Customer Billing Price
                              <Tooltip
                                placement="top"
                                content="Invoice price quoted to customer. Will be used for profit calculation."
                              >
                                <HiQuestionMarkCircle className="text-lg ml-2 text-primary-light dark:text-primary-dark hover:text-primary-hover transition-colors duration-200" />
                              </Tooltip>
                            </span>
                          }
                          value={billing_price}
                          onChange={handleBillingPriceChange}
                          placeholder="Billing price"
                          min={0}
                          max={10000}
                          disabled={sim_flag}
                          className={`!bg-light-surfaceSecondary dark:!bg-slate-800 transition-all duration-200 ${sim_flag ? "cursor-not-allowed opacity-60" : ""
                            }`}
                        />
                      </div>
                    </>
                  )}

                  {task?.item_id && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <Label
                          className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white block"
                          htmlFor="delayed_date"
                        >
                          Recommended Start Date
                        </Label>
                        <div className="mt-1">
                          <DatePicker
                            id="delayed_date"
                            className="bg-light-surface border border-light-border-subtle text-light-text-muted opacity-60 cursor-not-allowed text-sm focus:ring-primary-light focus:border-primary-light block w-full p-2.5 dark:bg-hover-dark dark:border-gray-600 dark:placeholder-gray-400 dark:text-gray-400 dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-lg transition-all duration-200"
                            selected={
                              task?.recommended_start_date
                                ? new Date(task?.recommended_start_date)
                                : null
                            }
                            autoComplete="off"
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div>
                        <Label
                          className="text-sm font-medium font-['Inter'] leading-tight mb-2 text-light-text-primary dark:text-white block"
                          htmlFor="delayed_date"
                        >
                          Adjusted Start Date
                        </Label>
                        <div className="mt-1">
                          <DatePicker
                            id="delayed_date"
                            className="bg-light-surfaceSecondary border border-light-border-default text-light-text-primary text-sm focus:ring-primary-light focus:border-primary-light block w-full p-2.5 dark:bg-slate-800 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-primary-dark dark:focus:border-primary-dark rounded-lg transition-all duration-200"
                            selected={
                              delayed_date ? new Date(delayed_date) : null
                            }
                            onChange={(date: any) => setDelayedDate(date)}
                            minDate={
                              task?.recommended_start_date
                                ? new Date(task?.recommended_start_date)
                                : null
                            }
                            filterDate={(date) => isWeekday(date, org_id)}
                            autoComplete="off"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            {!isToken && (
              <div className="px-6 py-4 items-center flex gap-4 mt-2 border-t rounded-t-md border-gray-500 dark:border-gray-700 fixed bottom-0 right-0 left-0 bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg">
                <CustomButton
                  variant="cancel"
                  onClick={handleCloseModal}
                  className="hover:bg-light-interactive-hover dark:hover:bg-hover-dark transition-all duration-200"
                >
                  Cancel
                </CustomButton>
                <div className="ml-auto flex items-center gap-4">
                  {task?.item_id && (
                    <UnsavedChangesIndicator hasUnsavedChanges={unsavedChanges} />
                  )}
                  <CustomButton
                    onClick={onSubmit}
                    disabled={taskLoading || isSubmitting || (task?.item_id && !unsavedChanges)}
                    isLoading={taskLoading || isSubmitting}
                    className="bg-gradient-to-r from-primary-light to-primary-dark hover:from-primary-hover hover:to-primary-dark text-white shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                  >
                    {isSubmitting
                      ? task?.item_id
                        ? "Updating..."
                        : "Creating..."
                      : task?.item_id
                        ? "Update"
                        : "Create"}
                  </CustomButton>
                </div>
              </div>
            )}
          </div>
        </Drawer>


        <TaskDeleteModal
          showModal={showDeleteConfirm}
          taskTitle={task?.task_title || selectedTask?.task_title}
          taskId={task?.item_id || selectedTask?.item_id || selectedTask?.id}
          setShowModal={setShowDeleteConfirm}
          handleConfirm={submitDeleteTask}
          isDeleting={isDeleting}
        />
      </Portal >
    </>
  );
};

export default TaskCompletionUpdateModal;
